import { format, isValid } from "date-fns";
import { merge, omit, snakeCase } from "lodash";

import { getWordProcessorInitialText } from "components/word-processor-field/word-processor-value.utils";
import { REPORT_TYPE_REM, REPORT_TYPE_TEST_BOX } from "constants/audiology";
import { WORD_PROCESSOR_DELIVERED_AT_DATE } from "constants/common";
import { UpdateReportValues } from "features/patients/sections/patient-audiology/update-report-values.type";
import { type ICD10Code } from "services/icd10-codes";
import {
  type AcousticReflexDecayReport,
  type AcousticReflexReport,
  type CustomTest,
  type CustomTestResponse,
  type PureToneAudiogramReport,
  type PureToneAudiogramReportArray,
  type PureToneAudiogramReportResponse,
  type Report,
  type ReportInput,
  type ReportResponse,
  type SpeechAudiometryReport,
  type SpeechInNoiseReport,
  type TympanometryReport,
  type WordRecognitionReport
} from "services/types/reports.type";
import {
  ObjectConversionInputType,
  camelCaseKeysToUnderscore,
  convertDateInTimezoneToCurrent,
  formatBackendDateOnly,
  getFullName,
  underScoreKeysToCamelCase
} from "utils/common";
import { convertMarkdownToHtml, isLikelyHtml } from "utils/markdown.utils";

import { convertAppointmentResponseToModel } from "./appointments.util";

const getCombinedArrayValue = (
  annotations: PureToneAudiogramReportArray,
  numerics: PureToneAudiogramReportArray
) => {
  const result: (string | null)[] = [];

  const count = Math.max(annotations?.length ?? 0, numerics?.length ?? 0);

  for (let i = 0; i < count; i++) {
    const annotation = annotations?.at(i) ?? null;
    const numeric = numerics?.at(i) ?? null;

    if (numeric === null) {
      result.push(annotation);
    } else if (annotation === null) {
      result.push(numeric);
    } else {
      result.push(`${annotation}_${numeric}`);
    }
  }

  return result;
};

const combineAnnotationsWithNumericValues = (pureToneAudiogramReport: any) => {
  const result: Record<string, any> = {};
  const FIELD_NAMES_TO_COMBINE = [
    "air_conduction_l",
    "air_conduction_masking_level_l",
    "bone_conduction_l",
    "bone_conduction_masking_level_l",
    "mcl_l",
    "ucl_l",
    "air_conduction_r",
    "air_conduction_masking_level_r",
    "bone_conduction_r",
    "bone_conduction_masking_level_r",
    "mcl_r",
    "ucl_r",
    "soundfield_aided_l",
    "soundfield_unaided_l",
    "soundfield_aided_r",
    "soundfield_unaided_r"
  ];

  Object.entries(pureToneAudiogramReport).forEach(([fieldName, value]) => {
    if (FIELD_NAMES_TO_COMBINE.includes(fieldName)) {
      result[fieldName] = getCombinedArrayValue(
        pureToneAudiogramReport[`${fieldName}_annotation`],
        value as PureToneAudiogramReportArray
      );
    } else if (
      fieldName === "audiogram_annotation" ||
      !fieldName.endsWith("_annotation")
    ) {
      result[fieldName] = value;
    }
  });

  return result;
};

function handleReportAttribute(
  key: keyof UpdateReportValues,
  value: string | Date | { id: number } | ObjectConversionInputType
) {
  const MODEL_ATTRS = [
    "appointment",
    "audiometer",
    "audiometerConfiguration",
    "referringPhysician",
    "primaryCarePhysician",
    "reviewer"
  ];
  const DATE_ATTRS = ["calibrationDate"];
  const NUMERIC_ATTRS = ["reliability", "testMethod", "transducer"];
  const TESTS_ATTRS = [
    "speechInNoiseReport",
    "tympanometryReport",
    "acousticReflexReport",
    "acousticReflexDecayReport",
    "speechAudiometryReport",
    "wordRecognitionReport",
    "additionalWordRecognitionReport",
    "pureToneAudiogramReport"
  ];
  if (MODEL_ATTRS.includes(key)) {
    if (
      typeof value === "object" &&
      value !== null &&
      !(value instanceof Date) &&
      "id" in value
    ) {
      return value.id;
    } else {
      return null;
    }
  }
  if (
    typeof value === "object" &&
    !(value instanceof Date) &&
    TESTS_ATTRS.includes(key)
  ) {
    const result = camelCaseKeysToUnderscore(value);

    return key === "pureToneAudiogramReport"
      ? combineAnnotationsWithNumericValues(result)
      : result;
  }
  if (DATE_ATTRS.includes(key)) {
    if (value instanceof Date) {
      return format(value as Date, "yyyy-MM-dd");
    } else {
      return null;
    }
  }
  if (NUMERIC_ATTRS.includes(key)) {
    return value ? Number(value) : null;
  }
  return value;
}

export const formatReportInput = (formData: any): Partial<ReportInput> => {
  const { diagnosis, ...data } = omit(
    Object.entries(formData).reduce((output, [key, value]) => {
      return {
        ...output,
        [snakeCase(key)]: handleReportAttribute(
          key as keyof UpdateReportValues,
          value as string | Date | { id: number }
        )
      };
    }, {}),
    "physicians"
  ) as any;

  const physicians =
    "physicians" in formData
      ? (formData.physicians ?? []).map((option: any) => ({
          id: option.id ?? option.physician_id,
          is_primary_care: Boolean(option.is_primary_care)
        }))
      : undefined;

  const icd10_codes =
    formData.diagnosis && "icd10Codes" in formData.diagnosis
      ? formData.diagnosis?.icd10Codes?.map(
          (icd10Code: ICD10Code) => icd10Code.id
        )
      : undefined;

  return {
    ...data,
    diagnosis: icd10_codes
      ? merge({}, omit(diagnosis, "icd10Codes"), { icd10_codes })
      : undefined,
    physicians
  };
};

const formatPureToneValues = (values: (string | null)[]) => {
  const original = values?.length
    ? values
    : Array.from({ length: 13 }, () => null);

  const result = original.map((value) => {
    if (value === null) return { annotation: null, numeric: null };

    const [annotation, numeric] = String(value).split("_");

    return {
      annotation: annotation ?? null,
      numeric: Number.isInteger(Number.parseInt(numeric))
        ? Number(numeric)
        : null
    };
  });

  return [
    result.map((item) => item.annotation),
    result.map((item) => item.numeric)
  ];
};

const formatPureToneAudiogramReport = (
  report?: PureToneAudiogramReportResponse | null
) => {
  if (!report) return report;

  const [air_conduction_l_annotation, air_conduction_l] = formatPureToneValues(
    report.air_conduction_l
  );

  const [
    air_conduction_masking_level_l_annotation,
    air_conduction_masking_level_l
  ] = formatPureToneValues(report.air_conduction_masking_level_l);

  const [bone_conduction_l_annotation, bone_conduction_l] =
    formatPureToneValues(report.bone_conduction_l);

  const [
    bone_conduction_masking_level_l_annotation,
    bone_conduction_masking_level_l
  ] = formatPureToneValues(report.bone_conduction_masking_level_l);

  const [mcl_l_annotation, mcl_l] = formatPureToneValues(report.mcl_l);

  const [ucl_l_annotation, ucl_l] = formatPureToneValues(report.ucl_l);

  const [soundfield_aided_l_annotation, soundfield_aided_l] =
    formatPureToneValues(report.soundfield_aided_l);

  const [soundfield_unaided_l_annotation, soundfield_unaided_l] =
    formatPureToneValues(report.soundfield_unaided_l);

  const [air_conduction_r_annotation, air_conduction_r] = formatPureToneValues(
    report.air_conduction_r
  );

  const [
    air_conduction_masking_level_r_annotation,
    air_conduction_masking_level_r
  ] = formatPureToneValues(report.air_conduction_masking_level_r);

  const [bone_conduction_r_annotation, bone_conduction_r] =
    formatPureToneValues(report.bone_conduction_r);

  const [
    bone_conduction_masking_level_r_annotation,
    bone_conduction_masking_level_r
  ] = formatPureToneValues(report.bone_conduction_masking_level_r);

  const [mcl_r_annotation, mcl_r] = formatPureToneValues(report.mcl_r);

  const [ucl_r_annotation, ucl_r] = formatPureToneValues(report.ucl_r);

  const [soundfield_aided_r_annotation, soundfield_aided_r] =
    formatPureToneValues(report.soundfield_aided_r);

  const [soundfield_unaided_r_annotation, soundfield_unaided_r] =
    formatPureToneValues(report.soundfield_unaided_r);

  const result = {
    ...report,
    air_conduction_l,
    air_conduction_l_annotation,

    air_conduction_masking_level_l,
    air_conduction_masking_level_l_annotation,

    bone_conduction_l,
    bone_conduction_l_annotation,

    bone_conduction_masking_level_l,
    bone_conduction_masking_level_l_annotation,

    mcl_l,
    mcl_l_annotation,

    ucl_l,
    ucl_l_annotation,

    soundfield_aided_l,
    soundfield_aided_l_annotation,

    soundfield_unaided_l,
    soundfield_unaided_l_annotation,

    air_conduction_r,
    air_conduction_r_annotation,

    air_conduction_masking_level_r,
    air_conduction_masking_level_r_annotation,

    bone_conduction_r,
    bone_conduction_r_annotation,

    bone_conduction_masking_level_r,
    bone_conduction_masking_level_r_annotation,
    mcl_r,
    mcl_r_annotation,

    ucl_r,
    ucl_r_annotation,

    soundfield_aided_r,
    soundfield_aided_r_annotation,

    soundfield_unaided_r,
    soundfield_unaided_r_annotation
  };
  return result;
};

export function checkIfSummaryIsJsonFromNoah(data: string): string {
  if (!data) {
    return data;
  }

  try {
    const parsed = JSON.parse(data);

    if (parsed && typeof parsed === "object" && "html" in parsed) {
      try {
        const content = parsed.html;
        // New summaries are stored as HTML; legacy summaries are markdown.
        // Use the shared heuristic so all detection sites agree.
        const htmlContent = isLikelyHtml(content)
          ? content
          : convertMarkdownToHtml(content);
        return htmlContent;
      } catch {
        return parsed.html;
      }
    }
  } catch {}

  return data;
}

export function convertReportResponseToModel(report: ReportResponse) {
  const date = report?.report_date || report.created_at;
  const isWordProcessorValue = date
    ? new Date(date) > WORD_PROCESSOR_DELIVERED_AT_DATE
    : true;

  const result = {
    id: report.id,
    type: report.type,
    audiometerConfiguration: report.audiometer_configuration,
    signedAt: report.signed_at,
    isSigned: Boolean(report.is_signed),
    clinic: report.clinic,
    created_at: report.created_at,
    reportDate: report.report_date,
    appointment: report.appointment
      ? convertAppointmentResponseToModel(report.appointment)
      : null,
    audiometer: report.audiometer,
    calibrationDate: report.calibration_date,
    coverLetter: report.cover_letter ?? "",
    includeCoverLetter: Boolean(report.include_cover_letter),
    reliability: report.reliability,
    testMethod: report.test_method,
    transducer: report.transducer,
    summary: isWordProcessorValue
      ? checkIfSummaryIsJsonFromNoah(report.summary)
      : getWordProcessorInitialText(report.summary),
    tympanometryReport: underScoreKeysToCamelCase(
      report.tympanometry_report as unknown as ObjectConversionInputType
    ) as unknown as TympanometryReport,
    acousticReflexReport: underScoreKeysToCamelCase(
      report.acoustic_reflex_report as unknown as ObjectConversionInputType
    ) as unknown as AcousticReflexReport,
    acousticReflexDecayReport: underScoreKeysToCamelCase(
      report.acoustic_reflex_decay_report as unknown as ObjectConversionInputType
    ) as unknown as AcousticReflexDecayReport,
    speechInNoiseReport: underScoreKeysToCamelCase(
      report.speech_in_noise_report as ObjectConversionInputType
    ) as SpeechInNoiseReport,
    speechAudiometryReport: underScoreKeysToCamelCase(
      report.speech_audiometry_report as unknown as ObjectConversionInputType
    ) as unknown as SpeechAudiometryReport,
    wordRecognitionReport: underScoreKeysToCamelCase(
      report.word_recognition_report as unknown as ObjectConversionInputType
    ) as unknown as WordRecognitionReport,
    additionalWordRecognitionReport: underScoreKeysToCamelCase(
      report.additional_word_recognition_report as unknown as ObjectConversionInputType
    ) as unknown as WordRecognitionReport,
    pureToneAudiogramReport: underScoreKeysToCamelCase(
      formatPureToneAudiogramReport(
        report.pure_tone_audiogram_report
      ) as unknown as ObjectConversionInputType
    ) as unknown as PureToneAudiogramReport,
    otoacoustic_emissions_report: report.otoacoustic_emissions_report,
    otoscopy_report: report.otoscopy_report,
    real_ear_measurement_report: report.real_ear_measurement_report,
    test_box_report: report.test_box_report,
    customTestReports: report.custom_test_reports,
    hearingLossLeft: report.hearing_loss_left,
    hearingLossRight: report.hearing_loss_right,
    hearingLossTypeLeft: report.hearing_loss_type_left,
    hearingLossTypeRight: report.hearing_loss_type_right,
    hearingLossShapeLeft: report.hearing_loss_shape_left,
    hearingLossShapeRight: report.hearing_loss_shape_right,
    diagnosis: report.diagnosis,
    icd10Codes: report.diagnosis?.icd10_codes,
    isDiagnosisSelected: Boolean(report.is_diagnosis_selected),
    isTestConfigurationSelected:
      report.is_test_configuration_selected !== false,
    isHearingAidsIncluded:
      report.is_hearing_aids_included === undefined
        ? true
        : Boolean(report.is_hearing_aids_included),
    referringPhysician: report.referring_physician,
    primaryCarePhysician: report.primary_care_physician,
    physicians: report.physicians,
    noah_session: report.noah_session,
    // Only read under NOAH_SESSION_PICKER_NEW_REPORT; absent otherwise.
    ...(report.noah_sync_failed_at !== undefined && {
      noah_sync_failed_at: report.noah_sync_failed_at
    }),
    include_tympanometry_graph: report.include_tympanometry_graph,
    tympanometry_scale_max: report.tympanometry_scale_max,
    tympanometry_scale_step: report.tympanometry_scale_step,
    reviewer: report.reviewer
      ? {
          ...report.reviewer,
          fullName: getFullName({
            firstName: report.reviewer?.first_name || "",
            lastName: report.reviewer?.last_name || "",
            title: report.reviewer?.title,
            suffix: report.reviewer?.suffix
          })
        }
      : null,
    created_by: report.created_by
      ? {
          ...report.created_by,
          fullName: getFullName({
            firstName: report.created_by.first_name || "",
            lastName: report.created_by.last_name || "",
            title: report.created_by.title
          })
        }
      : null
  } as Report;
  return result;
}

export const getReportTitleAdornment = (
  isSigned?: boolean,
  preview?: boolean
) => {
  if (preview && !isSigned) {
    return "(Preview)";
  } else if (!preview && !isSigned) {
    return "(Draft)";
  }
  return "";
};

export function convertCustomTestResponseToModel({
  id,
  name,
  details,
  is_selected
}: CustomTestResponse) {
  const customTest: CustomTest = {
    id,
    title: name,
    description: details,
    isSelected: is_selected
  };
  return customTest;
}

export const formatReportTitle = (
  report: Report | undefined,
  isPreview?: boolean
) => {
  if (!report) return "";

  const reportTitle = report.appointment
    ? `${report.appointment?.appointmentType?.name} Report - ${format(
        new Date(
          convertDateInTimezoneToCurrent(
            report.appointment.startDate,
            report.appointment?.clinic?.timezone
          ).toISOString()
        ),
        "MMMM dd yyyy"
      )}`
    : `Report - ${formatBackendDateOnly(
        report.reportDate ||
          report.noah_session?.session_date ||
          report.created_at,
        "MMMM dd yyyy"
      )}`;

  const stateAdornment = getReportTitleAdornment(report?.isSigned, isPreview);

  return `${reportTitle} ${stateAdornment}`.trim();
};

const REPORT_FILE_DATE_FORMAT = "yyyy-MM-dd";

// The clinic-local calendar day of a backend timestamp, or "" when it is missing or unusable
// (Noah sends year-0001 placeholders for sessions with no date).
const toClinicFileDate = (value: unknown, timezone?: string) => {
  if (value == null || String(value).trim() === "") {
    return "";
  }
  try {
    const instant = new Date(value as string | number | Date);
    if (!isValid(instant)) return "";
    const year = instant.getFullYear();
    if (year < 1900 || year > 2100) return "";
    let clinicDate = instant;
    try {
      const converted = convertDateInTimezoneToCurrent(instant, timezone);
      if (isValid(converted)) clinicDate = converted;
    } catch {
      // An unknown clinic timezone: keep the browser's own calendar day.
    }
    return format(clinicDate, REPORT_FILE_DATE_FORMAT);
  } catch {
    return "";
  }
};

/**
 * The visit date that goes into a report's file name, `yyyy-MM-dd`, as a calendar day in the
 * clinic's timezone. Same precedence as `formatReportTitle`: the appointment start, then the
 * report date, the Noah session date and the creation date. Falls back to today when none of
 * those is a usable date, so naming a file never fails.
 */
export const getReportFileDate = (report: Report | undefined | null) => {
  const timezone =
    report?.appointment?.clinic?.timezone || report?.clinic?.timezone;
  const candidates = [
    report?.appointment?.startDate,
    report?.reportDate,
    report?.noah_session?.session_date,
    report?.created_at
  ];

  for (const candidate of candidates) {
    const fileDate = toClinicFileDate(candidate, timezone || undefined);
    if (fileDate) return fileDate;
  }

  return format(new Date(), REPORT_FILE_DATE_FORMAT);
};

const getReportFileKind = (reportType?: number) => {
  if (reportType === REPORT_TYPE_REM) return "real-ear-measurement-report";
  if (reportType === REPORT_TYPE_TEST_BOX) return "hearing-aid-test-box-report";
  return "audiology-report";
};

/**
 * File name (no extension) for a report PDF: `First-Last-audiology-report-yyyy-MM-dd`, matching
 * `getChartNoteFileName`. Used by Download, Email, Fax and Save to E-Docs so all four agree.
 */
export const getReportFileName = (
  report: Report | undefined | null,
  patient?: { first_name?: string | null; last_name?: string | null } | null
) => {
  const nameParts = [patient?.first_name, patient?.last_name]
    .map((name) => (name ?? "").replace(/[^a-zA-Z]/g, ""))
    .filter(Boolean);

  return [
    ...nameParts,
    getReportFileKind(report?.type),
    getReportFileDate(report)
  ].join("-");
};

export function isEmpty<T>(
  ear: T | undefined,
  excludeAttributes: string[] = []
): boolean {
  if (!ear) return true;

  // Cast ear to an indexable type
  let earIndexable: { [key: string]: any } = ear;

  for (let key in earIndexable) {
    if (excludeAttributes.includes(key)) continue;
    if (earIndexable[key] != null && earIndexable[key] !== "") return false;
  }
  return true;
}
